import { useEffect } from "react";
import { buildSiteSeo } from "../../shared/seo";

const managedSelectors = [
  "meta[name='description']",
  "link[rel='canonical']",
  "meta[property='og:site_name']",
  "meta[property='og:type']",
  "meta[property='og:title']",
  "meta[property='og:description']",
  "meta[property='og:url']",
  "meta[property='og:image']",
  "meta[property='og:logo']",
  "meta[name='twitter:card']",
  "meta[name='twitter:title']",
  "meta[name='twitter:description']",
  "meta[name='twitter:image']"
];

const optionalManagedSelectors = [
  "meta[property='og:image:width']",
  "meta[property='og:image:height']",
  "meta[property='og:image:type']",
  "meta[property='og:image:alt']",
  "meta[name='twitter:image:alt']"
];

function ensureElement(selector) {
  let element = document.head.querySelector(selector);
  if (element) return element;

  if (selector.startsWith("link")) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
  } else {
    element = document.createElement("meta");
    const name = selector.match(/\[name='([^']+)'\]/)?.[1];
    const property = selector.match(/\[property='([^']+)'\]/)?.[1];
    if (name) element.setAttribute("name", name);
    if (property) element.setAttribute("property", property);
  }

  document.head.appendChild(element);
  return element;
}

function applySeo(metadata) {
  document.title = metadata.title;

  const values = {
    "meta[name='description']": metadata.description,
    "link[rel='canonical']": metadata.canonicalUrl,
    "meta[property='og:site_name']": metadata.siteName,
    "meta[property='og:type']": metadata.type,
    "meta[property='og:title']": metadata.title,
    "meta[property='og:description']": metadata.description,
    "meta[property='og:url']": metadata.canonicalUrl,
    "meta[property='og:image']": metadata.imageUrl,
    "meta[property='og:logo']": metadata.logoUrl,
    "meta[name='twitter:card']": metadata.twitterCard,
    "meta[name='twitter:title']": metadata.title,
    "meta[name='twitter:description']": metadata.description,
    "meta[name='twitter:image']": metadata.imageUrl
  };

  for (const selector of managedSelectors) {
    const element = ensureElement(selector);
    if (element.tagName === "LINK") {
      element.setAttribute("href", values[selector]);
    } else {
      element.setAttribute("content", values[selector]);
    }
  }

  const imageAlt = String(metadata.imageAlt || "").trim();
  const ogImageWidth = Number(metadata.ogImageWidth || 0);
  const ogImageHeight = Number(metadata.ogImageHeight || 0);
  const ogImageType = String(metadata.ogImageType || "").trim();
  const optionalValues = {
    "meta[property='og:image:width']": Number.isFinite(ogImageWidth) && ogImageWidth > 0 ? String(ogImageWidth) : "",
    "meta[property='og:image:height']": Number.isFinite(ogImageHeight) && ogImageHeight > 0 ? String(ogImageHeight) : "",
    "meta[property='og:image:type']": ogImageType,
    "meta[property='og:image:alt']": imageAlt,
    "meta[name='twitter:image:alt']": imageAlt
  };

  for (const selector of optionalManagedSelectors) {
    const value = optionalValues[selector];
    const existing = document.head.querySelector(selector);
    if (!value) {
      existing?.remove();
      continue;
    }
    const element = existing || ensureElement(selector);
    element.setAttribute("content", value);
  }
}

export function Seo({ metadata }) {
  useEffect(() => {
    applySeo(metadata || buildSiteSeo());
  }, [metadata]);

  return null;
}
